import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-toastify'

import { deletePhoto, recipePhotosQuery, setAsCover, uploadPhoto } from '@/api'
import { UserRecipePhoto } from '@/api/types'
import { resizeImage } from '@/utils/images'
import usePhotosModeration from '@/features/photos/usePhotosModeration'

type UploadOptions = {
  photoSource?: 'USER' | 'AI_GENERATED' | 'CURATED'
  isCover?: boolean
}

export default function useRecipePhoto(idRecipe: number) {
  const queryClient = useQueryClient()
  const PHOTOS_QUERY_KEY = [recipePhotosQuery(idRecipe).queryKey]
  const { rejectPhoto, approvePhoto } = usePhotosModeration({ recipeId: idRecipe })

  // Delete
  const deleteMutation = useMutation({
    mutationKey: PHOTOS_QUERY_KEY,
    mutationFn: (idRecipePhoto: number) => deletePhoto(idRecipePhoto),
    onMutate: async (idRecipePhoto: number) => {
      queryClient.cancelQueries({ queryKey: PHOTOS_QUERY_KEY })

      const previousPhotos = queryClient.getQueryData<UserRecipePhoto[]>(PHOTOS_QUERY_KEY)

      queryClient.setQueryData(
        recipePhotosQuery(idRecipe).queryKey,
        (oldData: UserRecipePhoto[]) => {
          if (!oldData) return

          return oldData.filter((photo) => photo.idRecipePhoto !== idRecipePhoto)
        },
      )

      return { previousPhotos }
    },
    onError: (error, _, context) => {
      if (context?.previousPhotos) {
        queryClient.setQueryData(PHOTOS_QUERY_KEY, context?.previousPhotos)
      }

      toast.error('Failed to delete photo')
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: PHOTOS_QUERY_KEY })
    },
  })

  // Upload
  const uploadMutation = useMutation({
    mutationFn: (formData: FormData) => uploadPhoto(idRecipe, formData),
    onSuccess: async ({ data }) => {
      queryClient.setQueryData(
        recipePhotosQuery(idRecipe).queryKey,
        (oldData: UserRecipePhoto[]) => {
          if (!oldData) return
          return [...oldData, ...data]
        },
      )
    },
    onError: () => {
      toast.error('Failed to upload photo')
    },
  })

  // Set as cover
  const coverMutation = useMutation({
    mutationFn: (idRecipe: number) => setAsCover(idRecipe),
    onSuccess: async (_, idRecipePhoto) => {
      queryClient.setQueryData(
        recipePhotosQuery(idRecipe).queryKey,
        (oldData: UserRecipePhoto[]) => {
          if (!oldData) return

          return oldData.map((photo) => ({
            ...photo,
            isCover: photo.idRecipePhoto === idRecipePhoto,
            latestModerationStatus:
              photo.idRecipePhoto === idRecipePhoto ? 'APPROVED' : photo.latestModerationStatus,
          }))
        },
      )
      queryClient.invalidateQueries({ queryKey: ['recipeThumbnail', idRecipe] })
    },
    onError: () => {
      toast.error('Failed to upload photo')
    },
  })

  return {
    isPending: uploadMutation.isPending,
    deletePhoto: deleteMutation.mutateAsync,
    uploadPhoto: async (photo: File, options: UploadOptions = {}) => {
      const resizedImage = await resizeImage(photo)

      const formData = new FormData()
      formData.append('files', resizedImage)
      formData.append('photoSource', options.photoSource || 'USER')
      formData.append('isCover', options.isCover ? 'true' : 'false')

      return await uploadMutation.mutateAsync(formData)
    },
    rejectPhoto,
    approvePhoto,
    setAsCover: coverMutation.mutateAsync,
  }
}
